import { PaginationReturn } from '@common/types/service';
import { useAllPublicEventsQuery, useGetLanguageSpokenOptionsQuery, usePrefetch } from '@event/services';
import { RegistrationEventListItem } from '@event/types';
import { useAuth } from '@hooks/useAuth';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { OverviewEventCard } from '@overview/components/display';
import { OverviewEventsCarousel } from '@overview/routes/Overview/OverviewEventsCarousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const ITEMS_PER_PAGE_MD = 4;
const ITEMS_PER_PAGE_SM = 1;

export const OverviewEvents = () => {
    //#region Hooks
    const theme = useTheme();
    const isMdOrAbove = useMediaQuery(theme.breakpoints.up('md'));
    const [emblaRef, emblaApi] = useEmblaCarousel({
        slidesToScroll: 'auto',
    });
    //#endregion

    //#region State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const previousIsMdOrAbove = useRef(isMdOrAbove);
    //#endregion

    //#region Data Fetching
    const { user, isUserLoading } = useAuth();

    const { isError: isPreviousPageUnavailable, isLoading: isPreviousPageLoading } = useAllPublicEventsQuery(
        {
            page_size: isMdOrAbove ? ITEMS_PER_PAGE_MD : ITEMS_PER_PAGE_SM,
            page: currentPage - 1,
            ordering: 'date_start',
        },
        {
            skip: currentPage === 1,
        }
    );
    const { data: upcomingEventsResponse, isFetching: isUpcomingEventsFetching } = useAllPublicEventsQuery({
        page_size: isMdOrAbove ? ITEMS_PER_PAGE_MD : ITEMS_PER_PAGE_SM,
        page: currentPage,
        ordering: 'date_start',
    });
    const { isError: isNextPageUnavailable, isLoading: isNextPageLoading } = useAllPublicEventsQuery({
        page_size: isMdOrAbove ? ITEMS_PER_PAGE_MD : ITEMS_PER_PAGE_SM,
        page: currentPage + 1,
        ordering: 'date_start',
    });
    const upcomingEvents = upcomingEventsResponse as PaginationReturn<RegistrationEventListItem> | undefined;

    const { data: languageOptions, isLoading: areLanguageOptionsLoading } = useGetLanguageSpokenOptionsQuery();

    const prefetchPublicEvent = usePrefetch('getPublicEvent');
    //#endregion

    //#region Processed Data
    const maxData = upcomingEvents?.count;
    const isLoading = isUpcomingEventsFetching || areLanguageOptionsLoading || isUserLoading;

    const Items = useMemo(
        () =>
            upcomingEvents?.results.map((event) => (
                <OverviewEventCard
                    key={`upcoming-${event.type}-${event.id}`}
                    data={{
                        ...event,
                        language_spoken: {
                            code:
                                event?.language_spoken?.toString().toLowerCase() === 'en'
                                    ? 'us_uk'
                                    : event.language_spoken ?? '',
                            label:
                                languageOptions?.find((langOption) => langOption.code === event.language_spoken)
                                    ?.name ?? '',
                        },
                    }}
                    hasUserData={!!user}
                    onMouseEnter={() => {
                        prefetchPublicEvent({ type: event.type, id: event.id });
                    }}
                />
            )) ?? [],
        [upcomingEvents, languageOptions, user]
    );
    //#endregion

    //#region Effects
    useEffect(() => {
        if (totalItems <= 0 && upcomingEvents?.count) {
            setTotalItems(upcomingEvents.count);
        }
    }, [upcomingEvents, totalItems]);

    useEffect(() => {
        if (previousIsMdOrAbove.current !== isMdOrAbove) {
            setCurrentPage(1);
            emblaApi?.scrollTo(0);
            previousIsMdOrAbove.current = isMdOrAbove;
        }
    }, [isMdOrAbove, emblaApi]);
    //#endregion

    return !!upcomingEvents?.count || isLoading ? (
        <Box component="section" px={{ xs: '20px', md: '65px' }} py="25px">
            <Stack direction="row" justifyContent="space-between" alignItems="start">
                <Typography
                    fontFamily="museo-sans"
                    fontSize={32}
                    fontWeight={600}
                    letterSpacing={-1}
                    mb="26px"
                    lineHeight="normal"
                >
                    <FormattedMessage id="events.upcoming" defaultMessage="Upcoming Events" />
                </Typography>
                <Stack direction="row" alignItems="center" spacing="5px">
                    <IconButton
                        color="black"
                        className="black-border p-[6.5px]"
                        onClick={() => {
                            if (isMdOrAbove) {
                                setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
                            } else {
                                emblaApi?.scrollPrev();
                            }
                        }}
                        disabled={
                            (isPreviousPageUnavailable || isPreviousPageLoading || currentPage === 1) && isMdOrAbove
                        }
                    >
                        <NavigateBeforeIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        color="black"
                        className="black-border p-[6.5px]"
                        onClick={() => {
                            if (isMdOrAbove) {
                                setCurrentPage((prev) =>
                                    prev < Math.ceil((maxData ?? 0) / ITEMS_PER_PAGE_MD) ? prev + 1 : prev
                                );
                            } else {
                                emblaApi?.scrollNext();
                            }
                        }}
                        disabled={
                            isNextPageUnavailable ||
                            isNextPageLoading ||
                            (currentPage === Math.ceil((maxData ?? 0) / ITEMS_PER_PAGE_MD) && isMdOrAbove)
                        }
                    >
                        <NavigateNextIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Stack>
            {isMdOrAbove && (
                <Stack direction="column" spacing="24px">
                    {isLoading
                        ? Array.from({ length: isMdOrAbove ? ITEMS_PER_PAGE_MD : ITEMS_PER_PAGE_SM }).map(
                              (_, index) => <OverviewEventCard key={index} loading />
                          )
                        : Items}
                </Stack>
            )}
            {!isMdOrAbove && (
                <OverviewEventsCarousel
                    emblaHook={[emblaRef, emblaApi]}
                    slides={totalItems <= 0 ? 2 : totalItems}
                    onSlideChange={(i) => setCurrentPage(i + 1)}
                />
            )}
        </Box>
    ) : null;
};
